/** SUNTRONIX ONE — Navigation Prototype Preview & Recommendation.
 *
 *  Reachable from `/preview/navigation`.  Documents Options A/B/C, scores each
 *  against the criteria in §45, and lets management flip the platform-wide
 *  shell preference (Classic vs ONE Spaces) instantly.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, LayoutGrid, LayoutDashboard, PanelTop, Check } from "lucide-react";

const SHELL_KEY = "st.shell";

const OPTIONS = [
  {
    id: "A",
    name: "ONE Spaces",
    tagline: "Space Switcher + Context Ribbon. No sidebar.",
    icon: LayoutGrid,
    score: { simplicity: 10, premium: 10, enterprise: 9, speed: 9, discovery: 8, power: 8, beginner: 9, screen: 10, mobile: 9, scale: 10 },
    strengths: [
      "Content dominates — chrome recedes.",
      "Every route lives in exactly one Space; ~1-2 clicks to anything.",
      "Feels like a modern technology operating system, not a menu tree.",
    ],
    tradeoffs: [
      "Very heavy Suntronix ops users lose an always-visible module list.",
    ],
    live: true,
    apply: "spaces",
  },
  {
    id: "B",
    name: "ONE Dock",
    tagline: "Five-icon fixed dock — Home · Work · Search · Create · More.",
    icon: LayoutDashboard,
    score: { simplicity: 9, premium: 8, enterprise: 7, speed: 8, discovery: 6, power: 7, beginner: 10, screen: 9, mobile: 10, scale: 7 },
    strengths: [
      "Consumer-grade familiarity; mobile parity native.",
      "Screen real-estate maximised for tables.",
    ],
    tradeoffs: [
      "Deep contextual navigation buried under overlays.",
      "Discoverability suffers for occasional users.",
    ],
    live: false,
    apply: null,
  },
  {
    id: "C",
    name: "Spaces + Dock hybrid",
    tagline: "Space Switcher in ONE Bar + tiny five-icon utility dock.",
    icon: PanelTop,
    score: { simplicity: 8, premium: 9, enterprise: 9, speed: 9, discovery: 8, power: 9, beginner: 8, screen: 8, mobile: 8, scale: 9 },
    strengths: [
      "Best of both — Spaces gravitas + Dock immediacy.",
      "Great for very frequent power users.",
    ],
    tradeoffs: [
      "Two navigation surfaces to design and maintain.",
      "Slightly busier chrome than pure Spaces.",
    ],
    live: false,
    apply: null,
  },
];

const CRITERIA = [
  ["simplicity", "Simplicity"],
  ["premium", "Premium feel"],
  ["enterprise", "Enterprise credibility"],
  ["speed", "Navigation speed"],
  ["discovery", "Discoverability"],
  ["power", "Power user"],
  ["beginner", "Beginner"],
  ["screen", "Screen space"],
  ["mobile", "Mobile"],
  ["scale", "Scalability"],
];

function totalScore(o) {
  return CRITERIA.reduce((a, [k]) => a + (o.score[k] || 0), 0);
}

export default function NavigationPreview() {
  const navigate = useNavigate();
  const current = (() => {
    try { return window.localStorage.getItem(SHELL_KEY) || "classic"; } catch { return "classic"; }
  })();
  const [active, setActive] = useState(current);

  const applyShell = (value) => {
    try {
      if (value === "classic") window.localStorage.removeItem(SHELL_KEY);
      else window.localStorage.setItem(SHELL_KEY, value);
    } catch { /* ignore */ }
    setActive(value);
    setTimeout(() => window.location.assign("/"), 200);
  };

  return (
    <div className="max-w-5xl space-y-6" data-testid="navigation-preview">
      <div className="st-page-header">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--st-text-muted)]">Product · Navigation</div>
          <h1 className="st-page-title">Navigation prototype preview</h1>
          <div className="st-page-context">
            Compare three navigation architectures for SUNTRONIX ONE. Change is instant and platform-wide — no code redeploy.
          </div>
        </div>
        <div className="text-[11px] text-[color:var(--st-text-muted)]">
          Current shell: <span className="font-semibold text-[color:var(--st-text)]">
            {active === "spaces" ? "ONE Spaces" : "Classic sidebar"}
          </span>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4" data-testid="option-cards">
        {OPTIONS.map((o) => {
          const t = totalScore(o);
          const applied = active === o.apply;
          const OIcon = o.icon;
          return (
            <div key={o.id} className="st-panel st-panel-lift p-5" data-testid={`option-${o.id}`}>
              <div className="flex items-center gap-2">
                <OIcon className="h-4 w-4 text-[color:var(--st-accent)]" strokeWidth={1.6} />
                <div className="text-[10px] uppercase tracking-[0.16em] text-[color:var(--st-text-subtle)]">Option {o.id}</div>
                {o.live && (
                  <span className="ml-auto text-[9px] uppercase tracking-[0.18em] bg-[color:var(--st-accent-soft)] text-[color:var(--st-accent)] px-1.5 rounded">
                    Recommended
                  </span>
                )}
              </div>
              <h3 className="font-heading text-[18px] font-medium tracking-[-0.01em] mt-2">{o.name}</h3>
              <p className="text-[12px] text-[color:var(--st-text-muted)] mt-1 min-h-[36px]">{o.tagline}</p>
              <div className="mt-3 flex items-baseline gap-2">
                <div className="text-[28px] font-heading font-semibold tabular-nums">{t}</div>
                <div className="text-[10px] uppercase tracking-[0.16em] text-[color:var(--st-text-muted)]">of 100</div>
              </div>
              <div className="mt-3 space-y-1">
                {CRITERIA.map(([k, label]) => (
                  <div key={k} className="flex items-center gap-2 text-[11px]">
                    <span className="w-28 text-[color:var(--st-text-muted)] shrink-0">{label}</span>
                    <div className="flex-1 h-1 bg-[color:var(--st-surface-alt)] rounded-full overflow-hidden">
                      <div className="h-full bg-[color:var(--st-text)]" style={{ width: `${(o.score[k] / 10) * 100}%` }} />
                    </div>
                    <span className="w-4 text-right tabular-nums text-[color:var(--st-text)]">{o.score[k]}</span>
                  </div>
                ))}
              </div>
              <div className="mt-4">
                <div className="text-[10px] uppercase tracking-[0.16em] text-[color:var(--st-text-subtle)] mb-1">Strengths</div>
                <ul className="text-[12px] text-[color:var(--st-text)] space-y-0.5 list-disc pl-4">
                  {o.strengths.map((s) => <li key={s}>{s}</li>)}
                </ul>
                <div className="text-[10px] uppercase tracking-[0.16em] text-[color:var(--st-text-subtle)] mt-3 mb-1">Trade-offs</div>
                <ul className="text-[12px] text-[color:var(--st-text-muted)] space-y-0.5 list-disc pl-4">
                  {o.tradeoffs.map((s) => <li key={s}>{s}</li>)}
                </ul>
              </div>
              <div className="mt-5 pt-4 border-t border-[color:var(--st-border)]">
                {o.live ? (
                  <button
                    onClick={() => applyShell(o.apply)}
                    data-testid={`apply-${o.id}`}
                    disabled={applied}
                    className={
                      "w-full h-9 rounded-md text-[12px] font-medium inline-flex items-center justify-center gap-1.5 " +
                      (applied
                        ? "bg-[color:var(--st-accent-soft)] text-[color:var(--st-accent)] cursor-default"
                        : "bg-[color:var(--st-text)] text-white hover:bg-[#17263C]")
                    }
                  >
                    {applied
                      ? (<><Check className="h-3.5 w-3.5" /> Currently active</>)
                      : "Enable this shell"}
                  </button>
                ) : (
                  <div className="text-center text-[11px] uppercase tracking-[0.16em] text-[color:var(--st-text-hint)]">
                    Design study · not implemented
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="st-panel p-5" data-testid="recommendation">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-[color:var(--st-accent)]" />
          <div className="text-[13px] font-semibold text-[color:var(--st-text)]">Recommendation</div>
        </div>
        <p className="text-[13px] text-[color:var(--st-text-muted)] mt-2 max-w-[68ch]">
          Option A · <b>ONE Spaces</b> scores the highest across the criteria weighted for a premium enterprise operating
          system.  It removes the traditional sidebar entirely, keeps navigation to one bar + one ribbon that never
          scrolls, and lets the content dominate.  Recommended for production; the Dock (B) and Hybrid (C) explorations
          remain on file for a future power-user mode.
        </p>
        <div className="mt-4 flex items-center gap-2">
          <button onClick={() => applyShell("classic")} data-testid="revert-classic"
            className="h-8 px-3 rounded-md border border-[color:var(--st-border)] text-[12px] font-medium text-[color:var(--st-text)] hover:bg-[color:var(--st-surface-alt)]">
            Revert to Classic sidebar
          </button>
          <button onClick={() => navigate("/")} data-testid="navpreview-close"
            className="h-8 px-3 rounded-md text-[12px] text-[color:var(--st-text-muted)] hover:text-[color:var(--st-text)]">
            Close preview
          </button>
        </div>
      </div>
    </div>
  );
}
